import { useMemo, useState, useEffect, useRef } from "react";
import { IconButton } from "@material-ui/core";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Collapse,
  TextField,
  FormControl,
  InputAdornment,
  styled,
} from "@mui/material";
import { ExpandMore, ExpandLess, Clear } from "@material-ui/icons";
import { ArrayChip } from "components";

const StyledListItem = styled(ListItem)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    cursor: "pointer",
  },
  overflow: "hidden",
  alignItems: "center",
  paddingTop: 0,
  paddingBottom: 0,
}));

const DropdownCombo = ({
  placeholder,
  label,
  selectedValue,
  data,
  onChange,
  width = "100%",
  hasMore,
  isLoading,
  onScrollToEnd,
  searchQuery,
  handleSearchChange,
  handleClearSearch,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const listRef = useRef(null);

  const handleClick = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;

    const lowerQuery = searchQuery.toLowerCase();
    return data.filter(item => {
      const composers = Array.isArray(item.composer) ? item.composer : [];
      return (
        item.title_song?.toLowerCase().includes(lowerQuery) ||
        composers.some(c => c.toLowerCase().includes(lowerQuery))
      );
    });
  }, [data, searchQuery]);

  // === Infinite Scroll ===
  const handleScroll = () => {
    const listEl = listRef.current;
    if (listEl) {
      const { scrollTop, scrollHeight, clientHeight } = listEl;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 100;
      if (nearBottom && hasMore && !isLoading) {
        onScrollToEnd?.();
      }
    }
  };

  useEffect(() => {
    const el = listRef.current;

    if (open && el) {
      el.addEventListener("scroll", handleScroll);

      return () => {
        el.removeEventListener("scroll", handleScroll);
      };
    }
  }, [open, hasMore, isLoading]);

  return (
    <FormControl fullWidth>
      <Box sx={{ position: "relative" }}>
        <List
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            width: width,
            maxWidth: width,
            marginTop: 0,
          }}
        >
          <StyledListItem button onClick={handleClick}>
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <Box sx={{ flexGrow: 1 }}>
                <ListItemText
                  primary={selectedValue?.title_song || placeholder}
                  primaryTypographyProps={{
                    sx: {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Box>
              {selectedValue && (
                <IconButton
                  size="small"
                  edge="end"
                  onClick={e => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                >
                  <Clear fontSize="small" />
                </IconButton>
              )}
              {open ? <ExpandLess /> : <ExpandMore />}
            </Box>
          </StyledListItem>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 1 }}>
              <TextField
                fullWidth
                size="small"
                label={label}
                disabled={disabled}
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch}>
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <List
              ref={listRef}
              sx={{
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {filteredData.map((item, index) => (
                <StyledListItem
                  key={item.title_song + index}
                  sx={{ pl: 2, py: 1 }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        onClick={() => {
                          onChange(item);
                          setOpen(false);
                        }}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {item.title_song}
                      </Typography>
                    }
                    secondary={<ArrayChip list={item.composer} max={2} />}
                  />
                </StyledListItem>
              ))}
              {!isLoading && filteredData.length === 0 && (
                <ListItem disableGutters sx={{ pl: 2, py: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Song not found.
                  </Typography>
                </ListItem>
              )}
              {isLoading && (
                <ListItem disableGutters sx={{ pl: 2, py: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Loading...
                  </Typography>
                </ListItem>
              )}
            </List>
          </Collapse>
        </List>
      </Box>
    </FormControl>
  );
};

export default DropdownCombo;
